import React, { useRef, useState, useEffect, useContext } from "react";
import axios from "axios";
import { AuthContext } from "../../../context/AuthContext";
import { useDbContext } from "../../../context/DbContext";
import WindowHeader from "../../Layout/WindowHeader/WindowHeader";
import SherComboBox from "../../ComboBoxes/SherComboBox";
import headeLogo from "../../../assets/images/shersoftnavLogo.png";
import { useSelector } from "react-redux";
import { selectActiveLocation } from "../../../app/locationSlice";
import { selectActiveFyid, selectActiveFy } from "../../../app/financialYearSlice";
import { useActiveFyLimits } from "../../../utils/fyDateValidation";

// Safe date parser: handles DD/MMM/YYYY, DD/MM/YYYY, YYYY-MM-DD, ISO etc.
const parseDateToYyyyMmDd = (dateStr, fallback = "") => {
  if (!dateStr) return fallback;
  const cleanStr = String(dateStr).trim().split(/[\sT]+/)[0];
  if (/^\d{4}-\d{2}-\d{2}$/.test(cleanStr)) return cleanStr;
  const parts = cleanStr.split(/[-/]/);
  if (parts.length === 3) {
    const day = parseInt(parts[0], 10);
    const year = parseInt(parts[2], 10);
    if (year > 1000) {
      let month = parseInt(parts[1], 10);
      if (isNaN(month)) {
        const abbr = ["jan","feb","mar","apr","may","jun","jul","aug","sep","oct","nov","dec"];
        month = abbr.indexOf(parts[1].toLowerCase()) + 1;
      }
      if (month >= 1 && month <= 12 && day >= 1 && day <= 31)
        return `${year}-${String(month).padStart(2,"0")}-${String(day).padStart(2,"0")}`;
    }
  }
  const d = new Date(cleanStr);
  if (!isNaN(d.getTime()))
    return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
  return fallback;
};
// ─── Sub-components ───────────────────────────────────────────────────────────

function DateRow({ fromDate, toDate, setFromDate, setToDate, minDate, maxDate, isDateInFy }) {
  return (
    <div className="grid grid-cols-[110px_1fr] items-center gap-[10px]">
      <label className="text-[11px] font-bold text-slate-700 font-['Inter']">Period</label>
      <div className="grid grid-cols-2 gap-[10px]">
        <div className="flex items-center gap-1.5">
          <span className="text-[10px] text-slate-400 font-semibold font-['Inter'] whitespace-nowrap">From</span>
          <input
            type="date"
            min={minDate}
            max={maxDate}
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            onBlur={(e) => {
              const val = e.target.value;
              if (val && !isDateInFy(val)) {
                alert("Date incompatible with financial year");
              }
            }}
            className="w-full h-[28px] px-2 border border-slate-300 rounded text-[11px] font-semibold text-slate-800 font-['Inter'] outline-none bg-white focus:border-blue-500 focus:ring-1 focus:ring-blue-400/20 transition-all"
          />
        </div>
        <div className="flex items-center gap-1.5">
          <span className="text-[10px] text-slate-400 font-semibold font-['Inter'] whitespace-nowrap">To</span>
          <input
            type="date"
            min={minDate}
            max={maxDate}
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            onBlur={(e) => {
              const val = e.target.value;
              if (val && !isDateInFy(val)) {
                alert("Date incompatible with financial year");
              }
            }}
            className="w-full h-[28px] px-2 border border-slate-300 rounded text-[11px] font-semibold text-slate-800 font-['Inter'] outline-none bg-white focus:border-blue-500 focus:ring-1 focus:ring-blue-400/20 transition-all"
          />
        </div>
      </div>
    </div>
  );
}

function FooterButtons({ onShow, onClose }) {
  return (
    <div className="flex justify-end items-center gap-2 border-t border-slate-200 bg-slate-50 px-4 py-2.5">
      <button
        onClick={onShow}
        className="flex items-center gap-1.5 px-4 h-[30px] rounded-md border border-slate-300 bg-white hover:bg-slate-50 active:scale-[0.98] transition-all cursor-pointer shadow-sm text-[11px] font-bold text-[#0f56d3] font-['Inter']"
      >
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#2E7D32" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="20 6 9 17 4 12" />
        </svg>
        Show Report
      </button>
      <button
        onClick={onClose}
        className="flex items-center gap-1.5 px-4 h-[30px] rounded-md border border-slate-300 bg-white hover:bg-slate-50 active:scale-[0.98] transition-all cursor-pointer shadow-sm text-[11px] font-bold text-slate-600 font-['Inter']"
      >
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#D32F2F" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10" />
          <line x1="15" y1="9" x2="9" y2="15" />
          <line x1="9" y1="9" x2="15" y2="15" />
        </svg>
        Exit
      </button>
    </div>
  );
}

// ─── Main Component ────────────────────────────────────────────────────────────
function AgentReportModel({ onClose, openReport, zIndex }) {
  const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
  const { agentCode } = useContext(AuthContext);
  const { dbCode } = useDbContext();
  
  const activeLocation = useSelector(selectActiveLocation);
  const activeFyid = useSelector(selectActiveFyid);
  const activeFy   = useSelector(selectActiveFy);
  const { minDate, maxDate, isDateInFy } = useActiveFyLimits();

  const getCurrentDate = () => {
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  };

  const today = getCurrentDate();

  const [agentRepData, setAgentRepData] = useState({
    fromDate: minDate || today,
    toDate:   (maxDate && today > maxDate) ? maxDate : today,
    reportType: "agentCommisionReport",
    phoneNo: "",
    agent: { value: "", label: "" },
  });

  // Sync financial year date limits when minDate/maxDate become available from Redux
  useEffect(() => {
    if (minDate) {
      setAgentRepData((prev) => ({
        ...prev,
        fromDate: (!prev.fromDate || prev.fromDate === today) ? minDate : prev.fromDate,
        toDate: prev.toDate || ((maxDate && today > maxDate) ? maxDate : today),
      }));
    }
  }, [minDate, maxDate, today]);

  const hasAlertedFyRef = useRef(false);
  useEffect(() => {
    if (activeFy && !hasAlertedFyRef.current) {
      if (!isDateInFy(today)) {
        alert("Current date is outside the active financial year");
        hasAlertedFyRef.current = true;
      }
    }
  }, [activeFy, today, isDateInFy]);

  const storedLocId = sessionStorage.getItem("loginLocationId") || localStorage.getItem("loginLocationId");
  const storedLocName = sessionStorage.getItem("loginLocation") || localStorage.getItem("loginLocation");
  const defaultLocId = activeLocation?.id || storedLocId;
  const defaultLocName = activeLocation?.name || storedLocName;

  const [location, setLocation] = useState(() => ({
    value: defaultLocId ? Number(defaultLocId) : 0,
    label: defaultLocName || (defaultLocId ? "SHOP" : "All Locations")
  }));

  const set = (key, val) => setAgentRepData((prev) => ({ ...prev, [key]: val }));

  const [agentOptions, setAgentOptions] = useState([]);
  const [locationOptions, setLocationOptions] = useState([]);

  useEffect(() => {
    const targetId = activeLocation?.id || storedLocId;
    const targetName = activeLocation?.name || storedLocName;
    if (targetId && (!location.value || location.value === 0)) {
      setLocation({ value: Number(targetId), label: targetName || "SHOP" });
    }
  }, [activeLocation, storedLocId, storedLocName]);

  useEffect(() => {
    const fetchAgents = async () => {
      try {
        const response = await axios.get(`${apiBaseUrl}/main/schsalesmanNames/${dbCode}`);
        const mapped = (response.data || []).map((item) => ({
          value: item.Auto ?? item.auto,
          label: item.Name || item.name,
        }));
        setAgentOptions(mapped);
      } catch (error) {
        console.error("Error fetching agent names:", error.message);
      }
    };
    const fetchLocations = async () => {
      try {
        const response = await axios.get(`${apiBaseUrl}/otherregistration/getoptions/${dbCode}`);
        const mapped = (response.data || [])
          .filter((item) => String(item.Type || item.type || "").toLowerCase() === "location")
          .map((item) => ({
            value: Number(item.Auto ?? item.auto ?? 0),
            label: item.Name || item.name || "",
          }));
        const opts = [{ value: 0, label: "All Locations" }, ...mapped];
        setLocationOptions(opts);

        if (opts.length > 0) {
          const targetId = activeLocation?.id || storedLocId;
          const match = targetId ? opts.find((opt) => Number(opt.value) === Number(targetId)) : null;
          if (match) {
            setLocation(match);
          }
        }
      } catch (error) {
        console.error("Error fetching locations:", error.message);
      }
    };
    if (dbCode) {
      fetchAgents();
      fetchLocations();
    }
  }, [apiBaseUrl, dbCode, activeLocation]);

  const handleShow = () => {
    if (!openReport) return;
    if (agentRepData.fromDate && !isDateInFy(agentRepData.fromDate)) {
      alert("From date incompatible with financial year");
      return;
    }
    if (agentRepData.toDate && !isDateInFy(agentRepData.toDate)) {
      alert("To date incompatible with financial year");
      return;
    }
    const resolvedLoc = location?.value !== undefined ? Number(location.value) : 0;
    const resolvedFyid = activeFyid || Number(sessionStorage.getItem("selectedFyId")) || Number(localStorage.getItem("selectedFyId")) || 0;

    let resolvedAgent = null;
    const currentAgentLabel = (agentRepData.agent?.label || agentRepData.agent?.value || "").toString().trim();
    if (currentAgentLabel) {
      const match = agentOptions.find(
        (opt) =>
          String(opt.label).toLowerCase() === currentAgentLabel.toLowerCase() ||
          String(opt.value) === String(agentRepData.agent?.value)
      );
      if (match) {
        resolvedAgent = { value: match.value, label: match.label, auto: match.value };
      } else {
        resolvedAgent = { value: agentRepData.agent?.value || currentAgentLabel, label: currentAgentLabel };
      }
    }

    const payload = {
      ...agentRepData,
      agent: resolvedAgent,
      agentId: resolvedAgent?.value || null,
      locationId: isNaN(resolvedLoc) ? 0 : resolvedLoc,
      location: isNaN(resolvedLoc) ? 0 : resolvedLoc,
      fyid: resolvedFyid,
    };
    openReport(payload);
  };

  const comboInputClass =
    "text-[11px] font-['Inter'] font-semibold text-slate-900 border border-slate-300 rounded focus:border-blue-500 focus:ring-1 focus:ring-blue-400/20";

  return (
    <div
      className="absolute inset-0 w-full h-full bg-black/40 flex items-center justify-center pointer-events-auto"
      style={{ zIndex }}
    >
      <div className="bg-white w-[480px] shadow-[0_8px_30px_rgb(0,0,0,0.12)] flex flex-col overflow-hidden rounded-xl border border-slate-200">

        {/* ── Header ── */}
        <WindowHeader
          title="Agent Report"
          logo={headeLogo}
          onClose={onClose}
          style={{ height: "36px" }}
          className="bg-[#0f56d3] text-white"
          titleClassName="text-xs font-semibold tracking-wide"
          closeBtnClassName="w-6 h-6 rounded-[5px] border-none mr-2 flex items-center justify-center bg-red-600 hover:bg-red-700 text-white transition-colors"
        />

        {/* ── Body ── */}
        <div className="p-4 flex flex-col gap-3 bg-slate-50/50">

          {/* Period */}
          <DateRow
            fromDate={agentRepData.fromDate}
            toDate={agentRepData.toDate}
            setFromDate={(v) => set("fromDate", v)}
            setToDate={(v) => set("toDate", v)}
            minDate={minDate}
            maxDate={maxDate}
            isDateInFy={isDateInFy}
          />

          <div className="w-full h-px bg-slate-200" />

          {/* Report Type */}
          <div className="grid grid-cols-[110px_1fr] items-center gap-[10px] w-full">
            <label className="text-[11px] font-bold text-slate-700 font-['Inter'] whitespace-nowrap">Report Type</label>
            <select
              value={agentRepData.reportType}
              onChange={(e) => set("reportType", e.target.value)}
              className="w-full h-[28px] px-2 border border-slate-300 rounded text-[11px] font-semibold text-slate-800 font-['Inter'] outline-none bg-white focus:border-blue-500 focus:ring-1 focus:ring-blue-400/20 transition-all cursor-pointer"
            >
              <option value="agentCommisionReport">Simple</option>
            </select>
          </div>

          {/* Select Agent */}
          <div className="grid grid-cols-[110px_1fr] items-center gap-[10px] w-full">
            <label className="text-[11px] font-bold text-slate-700 font-['Inter'] whitespace-nowrap">Select Agent</label>
            <SherComboBox
              options={agentOptions}
              value={agentRepData.agent?.label || agentRepData.agent?.value || ""}
              onSelect={(opt) => set("agent", opt)}
              onInputChange={(text) => {
                if (text === "") {
                  set("agent", { value: "", label: "" });
                } else {
                  const match = agentOptions.find(
                    (opt) => String(opt.label).toLowerCase() === String(text).trim().toLowerCase()
                  );
                  set("agent", match || { value: text, label: text });
                }
              }}
              onFreeInput={(text) => {
                if (text === "") {
                  set("agent", { value: "", label: "" });
                } else {
                  const match = agentOptions.find(
                    (opt) => String(opt.label).toLowerCase() === String(text).trim().toLowerCase()
                  );
                  set("agent", match || { value: text, label: text });
                }
              }}
              placeholder="Select agent..."
              inputClassName={comboInputClass}
              inputHeight="28px"
              inputWidth="100%"
            />
          </div>

          {/* Select Location */}
          <div className="grid grid-cols-[110px_1fr] items-center gap-[10px] w-full">
            <label className="text-[11px] font-bold text-slate-700 font-['Inter'] whitespace-nowrap">Location</label>
            <SherComboBox
              options={locationOptions}
              value={location?.label || ""}
              onSelect={(opt) => setLocation(opt)}
              onInputChange={(text) =>
                setLocation(text === "" ? { value: "", label: "" } : { value: text, label: text })
              }
              onFreeInput={(text) =>
                setLocation(text === "" ? { value: "", label: "" } : { value: text, label: text })
              }
              placeholder="Select location..."
              inputClassName={comboInputClass}
              inputHeight="28px"
              inputWidth="100%"
            />
          </div>

          {/* Phone Number */}
          <div className="grid grid-cols-[110px_1fr] items-center gap-[10px] w-full">
            <label className="text-[11px] font-bold text-slate-700 font-['Inter'] whitespace-nowrap">Phone Number</label>
            <input
              type="text"
              value={agentRepData.phoneNo}
              onChange={(e) => set("phoneNo", e.target.value)}
              placeholder="Enter phone number..."
              className="w-full h-[28px] px-2 border border-slate-300 rounded text-[11px] font-semibold text-slate-800 font-['Inter'] outline-none bg-white focus:border-blue-500 focus:ring-1 focus:ring-blue-400/20 transition-all"
            />
          </div>
        </div>

        {/* ── Footer ── */}
        <FooterButtons onShow={handleShow} onClose={onClose} />
      </div>
    </div>
  );
}

export default AgentReportModel;
